import React, { useState } from 'react';
import groupService from '../services/groupService';
import '../styles/group-modals.css';

const CreateGroupModal = ({ isOpen, onClose, onGroupCreated }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    maxMembers: 50
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1); // 1: form, 2: success
  const [createdGroupData, setCreatedGroupData] = useState(null); // Store created group data

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'maxMembers' ? parseInt(value) || 1 : value
    }));
  };

  // Validate form
  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Group name is required');
      return false;
    }
    if (formData.name.length < 3) {
      setError('Group name must be at least 3 characters');
      return false;
    }
    if (formData.name.length > 50) {
      setError('Group name must be less than 50 characters');
      return false;
    }
    if (formData.description.length > 200) {
      setError('Description must be less than 200 characters');
      return false;
    }
    if (formData.maxMembers < 2 || formData.maxMembers > 1000) {
      setError('Maximum members must be between 2 and 1000');
      return false;
    }
    return true;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      // Validate user session before attempting to create group
      const userData = localStorage.getItem('userData');
      const userId = localStorage.getItem('userId');
      const userToken = localStorage.getItem('userToken');
      
      console.log('🔍 CreateGroupModal: Validating user session before group creation');
      if (!userData || !userId || !userToken) {
        throw new Error('Your session has expired. Please log in again to create a group.');
      }
      
      console.log('📝 Creating group with data:', formData);
      const response = await groupService.createGroup(formData);
      
      if (response.success) {
        console.log('✅ Group created successfully:', response);
        setCreatedGroupData(response.data); // Store the created group data
        setStep(2);
        
        // Call success callback after a short delay to show success screen
        setTimeout(() => {
          onGroupCreated(response.data);
        }, 2000);
      } else {
        throw new Error(response.error || 'Failed to create group');
      }
    } catch (error) {
      console.error('❌ Error creating group:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (loading) return; // Prevent closing during creation
    
    setFormData({ name: '', description: '', maxMembers: 50 });
    setError(null);
    setStep(1);
    setCreatedGroupData(null); // Clear created group data
    onClose();
  };

  // Handle share group
  const handleShare = async (groupData) => {
    const shareData = groupService.shareGroup(groupData.groupCode, groupData.group.name);
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Join "${groupData.group.name}" Investment Group`,
          text: shareData.text,
          url: shareData.link
        });
      } catch (error) {
        // User cancelled share
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(shareData.text);
        alert('Group invite copied to clipboard!');
      } catch (error) {
        alert(`Group Code: ${shareData.code}\nShare this code with friends!`);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {step === 1 ? '🏗️ Create New Group' : '🎉 Group Created!'}
          </h2>
          <button 
            className="modal-close" 
            onClick={handleClose}
            disabled={loading}
          >
            ✕
          </button>
        </div>

        {step === 1 ? (
          // Step 1: Create group form
          <form onSubmit={handleSubmit} className="create-group-form">
            <div className="form-group">
              <label htmlFor="name">Group Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="e.g., Family Investment Club"
                maxLength={50}
                required
                disabled={loading}
                autoFocus
              />
              <small className="char-count">
                {formData.name.length}/50 characters
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="description">Description (Optional)</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe your investment group..."
                maxLength={200}
                rows={3}
                disabled={loading}
              />
              <small className="char-count">
                {formData.description.length}/200 characters
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="maxMembers">Maximum Members</label>
              <input
                type="number"
                id="maxMembers"
                name="maxMembers"
                value={formData.maxMembers}
                onChange={handleInputChange}
                min={2}
                max={1000}
                disabled={loading}
              />
              <small className="help-text">
                How many people can join this group? (2-1000)
              </small>
            </div>

            {error && (
              <div className="error-message">
                <span className="error-icon">⚠️</span>
                {error}
              </div>
            )}

            <div className="form-actions">
              <button 
                type="button" 
                onClick={handleClose}
                className="btn btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loading || !formData.name.trim()}
              >
                {loading ? (
                  <>
                    <div className="loading-spinner small" />
                    Creating...
                  </>
                ) : (
                  'Create Group'
                )}
              </button>
            </div>
          </form>
        ) : (
          // Step 2: Success screen
          <div className="success-content">
            <div className="success-icon">🎉</div>
            <h3>Group Created Successfully!</h3>
            <p>Your investment group is ready. Share the code below to invite friends:</p>
            
            <div className="group-code-display">
              <div className="group-code">
                <span className="code-label">Group Code:</span>
                <span className="code-number">
                  {createdGroupData?.groupCode || createdGroupData?.group?.groupCode || 'Loading...'}
                </span>
              </div>
              <button 
                onClick={() => {
                  const code = createdGroupData?.groupCode || createdGroupData?.group?.groupCode;
                  if (code) {
                    navigator.clipboard.writeText(code);
                    alert('Group code copied to clipboard!');
                  }
                }}
                className="copy-btn"
                title="Copy group code"
              >
                📋
              </button>
            </div>

            <div className="success-actions">
              <button 
                onClick={() => handleShare({ 
                  groupCode: createdGroupData?.groupCode || createdGroupData?.group?.groupCode || 'XXXX', 
                  group: { name: createdGroupData?.group?.name || formData.name } 
                })}
                className="btn btn-primary"
              >
                📤 Share Invite
              </button>
              <button 
                onClick={handleClose}
                className="btn btn-secondary"
              >
                View Group
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateGroupModal;